import {
  AfterContentInit,
  Component,
  ContentChildren,
  forwardRef,
  Input,
  OnDestroy,
  QueryList,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { ButtonToggleColor, ButtonToggleComponent, ButtonToggleSize } from './button-toggle.component';

@Component({
  selector: 'div[app-button-toggle-group]',
  template: '<ng-content></ng-content>',
  styleUrls: ['./button-toggle-group.component.scss'],
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ButtonToggleGroupComponent),
      multi: true,
    },
  ],
})
export class ButtonToggleGroupComponent implements ControlValueAccessor, AfterContentInit, OnDestroy {
  @ContentChildren(ButtonToggleComponent) toggleButtons: QueryList<ButtonToggleComponent>;

  @Input() color: ButtonToggleColor = 'primary';
  @Input() size: ButtonToggleSize = 'xs';

  private value: any;
  private isDisabled = false;
  private subscriptions: Subscription[] = [];
  private contentSub: Subscription;

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  ngAfterContentInit(): void {
    this.contentSub = this.toggleButtons.changes.pipe(startWith(this.toggleButtons)).subscribe(() => {
      this.clearSubscriptions();
      this.setupToggleButtons();
    });
  }

  ngOnDestroy(): void {
    this.clearSubscriptions();
    this.contentSub?.unsubscribe();
  }

  writeValue(value: any): void {
    this.value = value;
    this.updateCheckedState();
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.updateDisabledState();
  }

  private setupToggleButtons(): void {
    this.toggleButtons.forEach((toggle) => {
      toggle.color = this.color;
      toggle.size = this.size;
      toggle.disabled = this.isDisabled;
      toggle.checked = toggle.value === this.value;

      const sub = toggle.select.subscribe((value: any) => {
        this.value = value;
        this.onChange(value);
        this.onTouched();
        this.updateCheckedState();
      });

      this.subscriptions.push(sub);
    });
  }

  private updateCheckedState(): void {
    this.toggleButtons?.forEach((toggle) => {
      toggle.checked = toggle.value === this.value;
    });
  }

  private updateDisabledState(): void {
    this.toggleButtons?.forEach((toggle) => {
      toggle.disabled = this.isDisabled;
    });
  }

  private clearSubscriptions(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];
  }
}
